import React, { useState, useEffect } from 'react';
import { 
  Table, Button, Card, Space, Drawer, Typography, Tag, 
  Row, Col, Avatar, Divider, Tabs, Empty, Input,
  Spin, Descriptions, message, Tooltip, Modal, List, Radio, Badge, Select, DatePicker, Checkbox
} from 'antd';
import { 
  SearchOutlined, UserOutlined, PhoneOutlined, 
  MailOutlined, TagOutlined, InfoCircleOutlined,
  EnvironmentOutlined, GlobalOutlined, SyncOutlined,
  PlayCircleOutlined, HistoryOutlined, FilterOutlined, PlusOutlined, DeleteOutlined
} from '@ant-design/icons';
import { useAuth } from '../utils/auth';
import api from '../services/api';
import SopExecutionRecords from '../components/SopExecutionRecords';
import EmployeeSelect from '../components/common/EmployeeSelect';
import tagService from '../services/tagService';

// 辅助函数：按标签组对标签进行分类
const groupTagsByGroup = (tags = []) => {
  const groupedTags = {};
  
  // 如果标签为空，返回空数组
  if (!tags || tags.length === 0) {
    return [];
  }
  
  // 将标签按组分类
  tags.forEach(tag => {
    // 确保tag是有效对象
    if (!tag) return;
    
    const groupId = tag.groupId || 'default';
    const groupName = tag.groupName || '默认分组';
    
    if (!groupedTags[groupId]) {
      groupedTags[groupId] = {
        groupId,
        groupName,
        tags: []
      };
    }
    
    groupedTags[groupId].tags.push(tag);
  });
  
  // 转换为数组并排序
  return Object.values(groupedTags).sort((a, b) => a.groupName.localeCompare(b.groupName));
};

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option, OptGroup } = Select;
const { RangePicker } = DatePicker;

// 客户跟进关系过滤选项
const CUSTOMER_FILTER_OPTIONS = [
  { value: 'all', label: '所有客户' },
  { value: 'not_deleted_by_customer', label: '未被客户删除的客户' }
];

// 客户跟进关系服务
const customerService = {
  getAllCustomers: async (params) => {
    console.log('获取客户跟进关系列表，参数:', params);
    
    // 获取租户ID
    const tenantStr = localStorage.getItem('tenant');
    let tenantId;
    
    try {
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantId = tenant.id.toString();
      } else {
        throw new Error('找不到租户信息');
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
      throw new Error('租户信息解析失败，无法继续操作');
    }
    
    // 添加租户ID参数
    const queryParams = {
      ...params,
      tenantId: tenantId
    };
    
    try {
      // 调用新的客户跟进关系API
      const response = await api.get('/customer-follows', { params: queryParams });
      console.log('客户跟进关系列表响应数据:', response.data);
      
      // 处理响应数据，兼容不同的数据结构
      let result = {
        content: [],
        totalElements: 0,
        totalPages: 1,
        size: params.size || 10,
        number: params.page || 0
      };
      
      if (Array.isArray(response.data)) {
        // 如果返回的是数组，构造分页结构
        result.content = response.data;
        result.totalElements = response.data.length;
      } else if (response.data && typeof response.data === 'object') {
        // 如果返回的是对象，尝试提取分页信息
        if (Array.isArray(response.data.content)) {
          result = response.data;
        } else {
          // 如果没有content字段，尝试将整个对象作为内容
          result.content = [response.data];
          result.totalElements = 1;
        }
      }
      
      return result;
    } catch (error) {
      console.error('获取客户跟进关系列表错误:', error);
      if (error.response) {
        console.error('服务器响应错误:', error.response.data);
        throw new Error(`获取客户跟进关系列表失败: ${error.response.data.message || '服务器错误'}`);
      } else if (error.request) {
        console.error('请求未收到响应');
        throw new Error('获取客户跟进关系列表失败: 服务器无响应');
      } else {
        console.error('请求配置错误:', error.message);
        throw new Error(`获取客户跟进关系列表失败: ${error.message}`);
      }
    }
  },
  
  getCustomerById: async (id) => {
    console.log('获取客户跟进关系详情，ID:', id);
    
    // 获取租户ID
    const tenantStr = localStorage.getItem('tenant');
    let tenantId;
    
    try {
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantId = tenant.id.toString();
      } else {
        throw new Error('找不到租户信息');
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
      throw new Error('租户信息解析失败，无法继续操作');
    }
    
    try {
      // 调用新的客户跟进关系详情API
      const response = await api.get(`/customer-follows/${id}`, {
        params: { tenantId }
      });
      console.log('客户跟进关系详情响应数据:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('获取客户跟进关系详情错误:', error);
      if (error.response) {
        console.error('服务器响应错误:', error.response.data);
        throw new Error(`获取客户跟进关系详情失败: ${error.response.data.message || '服务器错误'}`);
      } else if (error.request) {
        console.error('请求未收到响应');
        throw new Error('获取客户跟进关系详情失败: 服务器无响应');
      } else {
        console.error('请求配置错误:', error.message);
        throw new Error(`获取客户跟进关系详情失败: ${error.message}`);
      }
    }
  },
  
  getCustomerTags: async (followId) => {
    console.log('获取客户跟进关系标签，跟进关系ID:', followId);
    
    // 获取租户ID
    const tenantStr = localStorage.getItem('tenant');
    let tenantId;
    
    try {
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantId = tenant.id.toString();
      } else {
        throw new Error('找不到租户信息');
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
      throw new Error('租户信息解析失败，无法继续操作');
    }
    
    try {
      // 调用新的客户跟进关系标签API
      const response = await api.get(`/customer-follows/${followId}/tags`, {
        params: { tenantId }
      });
      console.log('客户跟进关系标签响应数据:', response.data);
      
      return response.data;
    } catch (error) {
      console.error('获取客户跟进关系标签错误:', error);
      if (error.response) {
        console.error('服务器响应错误:', error.response.data);
        throw new Error(`获取客户跟进关系标签失败: ${error.response.data.message || '服务器错误'}`);
      } else if (error.request) {
        console.error('请求未收到响应');
        throw new Error('获取客户跟进关系标签失败: 服务器无响应');
      } else {
        console.error('请求配置错误:', error.message);
        throw new Error(`获取客户跟进关系标签失败: ${error.message}`);
      }
    }
  },
  
  syncCustomers: async () => {
    console.log('同步客户数据...');
    
    // 获取租户ID（而不是corpId）
    const tenantStr = localStorage.getItem('tenant');
    let tenantId;
    
    try {
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantId = tenant.id;
      } else {
        throw new Error('找不到租户信息');
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
      throw new Error('租户信息解析失败，无法继续操作');
    }
    
    try {
      // 调用正确的同步API，传递tenantId参数
      const response = await api.post('/sync/customers/async', null, {
        params: {
          tenantId: tenantId,
          sourceType: 'WEWORK',
          onlyActiveEmployees: true
        }
      });
      console.log('同步客户响应:', response.data);
      return response.data;
    } catch (error) {
      console.error('同步客户失败:', error);
      if (error.response) {
        throw new Error(`同步失败: ${error.response.data.message || error.response.data || '服务器错误'}`);
      } else if (error.request) {
        throw new Error('同步失败: 服务器无响应');
      } else {
        throw new Error(`同步失败: ${error.message}`);
      }
    }
  },
  
  getSopTemplates: async () => {
    try {
      // 使用正确的API路径获取客户类型的推送
      const response = await api.get('/v1/sop/templates', {
        params: { status: 'active', deleted: false, targetType: 'CUSTOMER' }
      });
      return response.data;
    } catch (error) {
      console.error('获取推送列表错误:', error);
      // 如果带参数请求失败，尝试不带参数请求，但在前端过滤
      try {
        const fallbackResponse = await api.get('/v1/sop/templates');
        // 在前端过滤客户类型模板
        let result = fallbackResponse.data;
        if (Array.isArray(result)) {
          return result.filter(template => template.targetType === 'CUSTOMER');
        } else if (result && Array.isArray(result.content)) {
          result.content = result.content.filter(template => template.targetType === 'CUSTOMER');
          return result;
        }
        return result;
      } catch (fallbackError) {
        console.error('备选推送请求也失败:', fallbackError);
        throw error;
      }
    }
  },
  
  triggerSop: async (templateId, targetType, targetId, operatorId) => {
    const tenantStr = localStorage.getItem('tenant');
    let tenantId;
    
    try {
      if (tenantStr) {
        const tenant = JSON.parse(tenantStr);
        tenantId = tenant.id.toString();
      } else {
        throw new Error('找不到租户信息');
      }
    } catch (error) {
      console.error('解析tenant信息失败:', error);
      throw new Error('租户信息解析失败，无法继续操作');
    }
    
    const currentUser = JSON.parse(localStorage.getItem('currentUser') || '{}');
    const userId = currentUser.id || 1;
    
    if (!operatorId) {
      throw new Error('操作者ID不能为空，无法触发推送');
    }
    
    return await api.post('/v1/sop/trigger', {
      templateId,
      targetType,
      targetId,
      userId,
      operatorId  // 传递跟进人的企微userid
    });
  },
};

// 添加客户来源定义，用于显示友好的添加方式描述
const ADD_WAY_MAP = {
  0: '未知来源',
  1: '扫描二维码',
  2: '搜索手机号',
  3: '名片分享',
  4: '群聊',
  5: '手机通讯录',
  6: '微信联系人',
  8: '安装第三方应用时自动添加的客服人员',
  9: '搜索邮箱',
  10: '视频号添加',
  11: '通过日程参与人添加',
  12: '通过会议参与人添加',
  13: '添加微信好友对应的企业微信',
  14: '通过智慧硬件专属客服添加',
  15: '通过上门服务客服添加',
  16: '通过获客链接添加',
  17: '通过定制开发添加',
  18: '通过需求回复添加',
  21: '通过第三方售前客服添加',
  22: '通过可能的商务伙伴添加',
  24: '通过接受微信账号收到的好友申请添加',
  201: '内部成员共享',
  202: '管理员/负责人分配'
};

// 客户类型定义
const CUSTOMER_TYPE_MAP = {
  1: '微信用户',
  2: '企业微信用户'
};

// 性别定义
const GENDER_MAP = {
  0: '未知',
  1: '男',
  2: '女'
};

const Customers = () => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const { tenant } = useAuth(); // 从认证上下文获取当前租户信息
  const [searchText, setSearchText] = useState('');
  const [tags, setTags] = useState([]);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerCustomer, setDrawerCustomer] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10
  });
  const [sopTemplates, setSopTemplates] = useState([]);
  const [sopModalVisible, setSopModalVisible] = useState(false);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [triggerLoading, setTriggerLoading] = useState(false);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  // 新增状态：SOP执行记录抽屉
  const [sopRecordDrawerVisible, setSopRecordDrawerVisible] = useState(false);
  const [sopRecordCustomerId, setSopRecordCustomerId] = useState(null);
  const [customerFilter, setCustomerFilter] = useState('all'); // 新增客户过滤状态
  
  // 新增筛选条件状态
  const [selectedEmployee, setSelectedEmployee] = useState(null); // 选中的跟进成员（员工ID）
  const [selectedEmployeeUserid, setSelectedEmployeeUserid] = useState(null); // 对应的企微userid
  const [selectedTags, setSelectedTags] = useState([]); // 选中的标签
  const [dateRange, setDateRange] = useState([]); // 添加时间范围
  const [availableTags, setAvailableTags] = useState([]); // 可选标签列表
  
  // 批量操作相关状态
  const [selectedRowKeys, setSelectedRowKeys] = useState([]); // 选中的行键
  const [selectedRows, setSelectedRows] = useState([]); // 选中的行数据
  const [selectAllPages, setSelectAllPages] = useState(false); // 是否跨页全选
  const [batchTagModalVisible, setBatchTagModalVisible] = useState(false); // 批量标签操作弹窗
  const [batchOperationType, setBatchOperationType] = useState(null); // 批量操作类型：'add' | 'remove'
  const [batchSelectedTags, setBatchSelectedTags] = useState([]); // 批量操作选中的标签
  const [batchOperationLoading, setBatchOperationLoading] = useState(false);
  
  // 辅助函数：将员工ID转换为userid
  const convertEmployeeIdToUserid = async (employeeId) => {
    if (!employeeId) return null;
    
    try {
      const response = await api.get(`/wework/employees/${employeeId}`);
      return response.data?.userid || employeeId;
    } catch (error) {
      console.error('获取员工userid失败，使用原值:', error);
      return employeeId;
    }
  };

  // 加载客户列表
  const fetchCustomers = async (page = pagination.current, pageSize = pagination.pageSize, name = searchText, filter = customerFilter, weworkUserid = selectedEmployeeUserid, tags = selectedTags, dateRangeFilter = dateRange) => {
    try {
      setLoading(true);
      const params = {
        page: page - 1, // 后端分页通常从0开始
        size: pageSize,
        sort: 'createdAt,desc'
      };
      
      if (name) {
        params.name = name;
      }

      // 新增：根据过滤条件添加参数
      if (filter === 'not_deleted_by_customer') {
        params.onlyNotDeletedByCustomer = true;
      }
      
      // 新增：跟进成员筛选
      if (weworkUserid) {
        params.weworkUserid = weworkUserid;
        console.log('添加跟进成员筛选参数:', { weworkUserid: weworkUserid });
      }
      
      // 新增：标签筛选
      if (tags && tags.length > 0) {
        params.tagIds = tags.join(',');
        console.log('添加标签筛选参数:', { tagIds: tags.join(',') });
      }
      
      // 新增：添加时间筛选
      if (dateRangeFilter && dateRangeFilter.length === 2) {
        params.startDate = dateRangeFilter[0].format('YYYY-MM-DD');
        params.endDate = dateRangeFilter[1].format('YYYY-MM-DD');
      }
      
      const data = await customerService.getAllCustomers(params);
      console.log('处理后的客户数据:', data);
      
      // 映射客户跟进关系数据，确保字段名一致
      const mappedCustomers = (data.content || []).map(follow => {
        console.log('单个客户跟进关系原始数据:', follow);
        return {
          id: follow.id, // 这里是跟进关系的ID
          customerId: follow.customerId, // 系统客户ID
          weworkCustomerId: follow.weworkCustomerId, // 企微客户ID
          weworkUserid: follow.weworkUserid, // 企微成员ID
          name: follow.customerName, // 客户姓名
          phone: follow.customerPhone, // 客户手机号
          email: follow.customerEmail, // 客户邮箱
          company: follow.customerCompany, // 客户公司
          title: follow.customerPosition, // 客户职位
          status: 'active', // 跟进关系状态
          source: 'wework', // 来源统一为企微
          sourceId: follow.weworkUserid, // 使用企微成员ID作为sourceId
          customerCode: follow.customerId, // 使用系统客户ID作为客户编码
          gender: follow.customerGender, // 客户性别
          avatar: follow.customerAvatar, // 客户头像
          createdAt: follow.createdAt, // 跟进关系创建时间
          updatedAt: follow.updatedAt, // 跟进关系更新时间
          // 跟进关系特有字段
          employeeName: follow.employeeName, // 企微成员姓名
          employeeAvatar: follow.employeeAvatar, // 企微成员头像
          remark: follow.remark, // 跟进关系备注
          description: follow.description, // 跟进关系描述
          state: follow.state, // 渠道参数
          type: follow.customerType, // 客户类型：1-微信用户，2-企业微信用户
          remarkMobiles: follow.remarkMobiles || [], // 备注手机号
          addWay: follow.addWay, // 添加方式
          createTime: follow.createTime, // 添加时间
          // 修复标签数据映射：后端已补充tag_name字段
          tags: (follow.tags || []).map(tag => ({
            id: tag.tag_id || tag.id,
            name: tag.tag_name || tag.name || '未知标签',
            color: 'blue',
            groupId: tag.group_id || tag.groupId,
            groupName: tag.group_name || tag.groupName
          }))
        };
      });
      
      console.log('映射后的客户数据:', mappedCustomers);
      setCustomers(mappedCustomers);
      setTotal(data.totalElements || mappedCustomers.length);
      setPagination({
        ...pagination,
        current: page,
        pageSize: pageSize,
        total: data.totalElements || mappedCustomers.length
      });
      
    } catch (error) {
      console.error('获取客户列表失败:', error);
      message.error(error.message || '获取客户列表失败');
    } finally {
      setLoading(false);
    }
  };


  // 加载可用标签列表
  const fetchAvailableTags = async () => {
    try {
      const tags = await tagService.getAllTags();
      console.log('获取到的标签列表:', tags);
      setAvailableTags(tags || []);
    } catch (error) {
      console.error('获取标签列表失败:', error);
      message.error('获取标签列表失败');
    }
  };

  useEffect(() => {
    fetchCustomers();
    fetchAvailableTags();
  }, []);

  // 新增：当过滤条件变化时重新加载数据
  useEffect(() => {
    if (customerFilter !== 'all') {
      fetchCustomers(1, pagination.pageSize, searchText, customerFilter, selectedEmployeeUserid, selectedTags, dateRange);
    } else {
      fetchCustomers(1, pagination.pageSize, searchText, customerFilter, selectedEmployeeUserid, selectedTags, dateRange);
    }
  }, [customerFilter]);

  // 辅助函数：解析日期或时间字符串
  const parseDateTime = (dateTimeStr) => {
    if (!dateTimeStr) return null;
    try {
      // 直接解析ISO格式日期字符串（不乘以1000）
      return new Date(dateTimeStr);
    } catch (e) {
      console.error('日期解析失败:', dateTimeStr, e);
      return null;
    }
  };

  // 辅助函数：格式化日期显示
  const formatDateTime = (dateTimeStr) => {
    const date = parseDateTime(dateTimeStr);
    if (!date || isNaN(date.getTime())) return '-';
    return date.toLocaleString();
  };

  // 同步客户数据
  const handleSync = async () => {
    try {
      setSyncLoading(true);
      const result = await customerService.syncCustomers();
      message.success('客户数据同步成功');
      
      // 同步完成后刷新列表
      setTimeout(() => {
        fetchCustomers(pagination.current, pagination.pageSize, searchText, customerFilter, selectedEmployeeUserid, selectedTags, dateRange);
      }, 1000);
    } catch (error) {
      console.error('同步客户数据失败:', error);
      message.error(error.message || '同步客户数据失败');
    } finally {
      setSyncLoading(false);
    }
  };

  // 显示客户详情抽屉
  const showCustomerDetail = async (customerId) => {
    try {
      setDrawerVisible(true);
      setDrawerLoading(true);
      
      // 获取客户详情
      const customer = await customerService.getCustomerById(customerId);
      console.log('获取到的客户详情原始数据:', customer);
      
      // 映射客户跟进关系详情数据，确保字段名一致
      const mappedCustomer = {
        id: customer.id, // 跟进关系ID
        customerId: customer.customerId, // 系统客户ID
        weworkCustomerId: customer.weworkCustomerId, // 企微客户ID
        weworkUserid: customer.weworkUserid, // 企微成员ID
        name: customer.customerName, // 客户姓名
        phone: customer.customerPhone, // 客户手机号
        email: customer.customerEmail, // 客户邮箱
        company: customer.customerCompany, // 客户公司
        title: customer.customerPosition, // 客户职位
        customerCode: customer.customerId, // 使用系统客户ID作为客户编码
        gender: customer.customerGender, // 客户性别
        status: 'active', // 跟进关系状态
        source: 'wework', // 来源统一为企微
        sourceId: customer.weworkUserid, // 企微成员ID
        sourceSystem: 'WEWORK',
        weworkExternalUserId: customer.weworkUserid, // 企微成员ID
        weworkType: 'follow_relationship',
        createdAt: customer.createdAt, // 跟进关系创建时间
        updatedAt: customer.updatedAt, // 跟进关系更新时间
        syncedAt: customer.updatedAt, // 同步时间
        avatar: customer.customerAvatar, // 客户头像
        // 跟进关系特有字段
        employeeName: customer.employeeName, // 企微成员姓名
        employeeAvatar: customer.employeeAvatar, // 企微成员头像
        type: customer.customerType, // 客户类型：1-微信用户，2-企业微信用户
        remark: customer.remark, // 跟进关系备注
        description: customer.description, // 跟进关系描述
        state: customer.state, // 渠道参数
        remarkMobiles: customer.remarkMobiles || [], // 备注手机号
        addWay: customer.addWay, // 添加方式
        createTime: customer.createTime, // 添加时间
        // 修复标签数据映射：后端已补充tag_name字段
        tags: Array.isArray(customer.tags) ? customer.tags.map(tag => ({
          id: tag.tag_id || tag.id,
          name: tag.tag_name || tag.name || '未知标签',
          color: 'blue',
          groupId: tag.group_id || tag.groupId,
          groupName: tag.group_name || tag.groupName
        })) : []
      };
      
      console.log('映射后的客户详情数据:', mappedCustomer);
      setDrawerCustomer(mappedCustomer);
      
              // 如果跟进关系详情中没有包含标签数据，单独请求标签
        if (!Array.isArray(customer.tags) || customer.tags.length === 0) {
          try {
            const tagsData = await customerService.getCustomerTags(customer.id); // 使用跟进关系ID
            console.log('获取到的客户跟进关系标签数据:', tagsData);
            
            // 如果标签是数组，则直接使用；否则尝试提取
            let tags = [];
            if (Array.isArray(tagsData)) {
              tags = tagsData;
            } else if (tagsData && Array.isArray(tagsData.tags)) {
              tags = tagsData.tags;
            }
            
            // 映射标签数据：后端已补充tag_name字段
            const mappedTags = tags.map(tag => ({
              id: tag.tag_id || tag.id,
              name: tag.tag_name || tag.name || '未知标签',
              color: 'blue',
              groupId: tag.group_id || tag.groupId,
              groupName: tag.group_name || tag.groupName
            }));
            
            // 将标签添加到客户数据中
            mappedCustomer.tags = mappedTags;
            setDrawerCustomer({...mappedCustomer});
          } catch (tagError) {
            console.error('获取客户跟进关系标签失败:', tagError);
            // 标签获取失败不影响整体流程，保持空数组
          }
        }
      
    } catch (error) {
      console.error('获取客户详情失败:', error);
      message.error(error.message || '获取客户详情失败');
    } finally {
      setDrawerLoading(false);
    }
  };

  // 关闭抽屉
  const handleDrawerClose = () => {
    setDrawerVisible(false);
  };

  // 处理表格分页变化
  const handleTableChange = (paginationInfo, filters, sorter) => {
    fetchCustomers(paginationInfo.current, paginationInfo.pageSize, searchText, customerFilter, selectedEmployeeUserid, selectedTags, dateRange);
  };

  // 过滤客户（已移除，搜索现在通过筛选按钮触发）
  // const handleSearch = (value) => {
  //   setSearchText(value);
  //   fetchCustomers(1, pagination.pageSize, value, customerFilter, selectedEmployeeUserid, selectedTags, dateRange);
  // };

  // 新增：处理客户过滤条件变化
  const handleCustomerFilterChange = (value) => {
    setCustomerFilter(value);
    // useEffect会自动处理重新加载数据
  };

  // 新增：处理跟进成员变化（不自动触发查询）
  const handleEmployeeChange = async (value) => {
    setSelectedEmployee(value);
    
    // 如果选择了员工，需要将员工ID转换为userid
    const weworkUserid = await convertEmployeeIdToUserid(value);
    setSelectedEmployeeUserid(weworkUserid);
  };

  // 新增：处理标签变化（不自动触发查询）
  const handleTagsChange = (values) => {
    setSelectedTags(values);
  };

  // 新增：处理日期范围变化（不自动触发查询）
  const handleDateRangeChange = (dates) => {
    setDateRange(dates);
  };

  // 新增：执行筛选
  const handleApplyFilters = () => {
    fetchCustomers(1, pagination.pageSize, searchText, customerFilter, selectedEmployeeUserid, selectedTags, dateRange);
  };

  // 新增：重置筛选条件
  const handleResetFilters = () => {
    setSelectedEmployee(null);
    setSelectedEmployeeUserid(null);
    setSelectedTags([]);
    setDateRange([]);
    setSearchText('');
    setCustomerFilter('all');
    // 重置时传null作为weworkUserid
    fetchCustomers(1, pagination.pageSize, '', 'all', null, [], []);
  };

  // 批量操作相关函数
  const handleSelectChange = (selectedRowKeys, selectedRows) => {
    setSelectedRowKeys(selectedRowKeys);
    setSelectedRows(selectedRows);
    // 如果当前是跨页全选状态，但用户取消了某些选择，则退出跨页全选状态
    if (selectAllPages && selectedRowKeys.length < customers.length) {
      setSelectAllPages(false);
    }
  };

  // 处理跨页全选
  const handleSelectAllPages = (checked) => {
    if (checked) {
      setSelectAllPages(true);
      // 选中当前页所有数据
      const currentPageKeys = customers.map(item => item.id);
      const currentPageRows = customers.slice();
      setSelectedRowKeys(currentPageKeys);
      setSelectedRows(currentPageRows);
    } else {
      setSelectAllPages(false);
      setSelectedRowKeys([]);
      setSelectedRows([]);
    }
  };

  // 显示批量标签操作弹窗
  const showBatchTagModal = (operationType) => {
    if (selectedRowKeys.length === 0 && !selectAllPages) {
      message.warning('请先选择要操作的客户');
      return;
    }
    setBatchOperationType(operationType);
    setBatchSelectedTags([]);
    setBatchTagModalVisible(true);
  };

  // 关闭批量标签操作弹窗
  const closeBatchTagModal = () => {
    setBatchTagModalVisible(false);
    setBatchOperationType(null);
    setBatchSelectedTags([]);
  };

  // 执行批量标签操作
  const executeBatchTagOperation = async () => {
    if (batchSelectedTags.length === 0) {
      message.warning('请选择要操作的标签');
      return;
    }

    setBatchOperationLoading(true);
    
    try {
      // 构建请求数据
      const requestData = {
        tagIds: batchSelectedTags,
        operationType: batchOperationType, // 'add' 或 'remove'
      };

      // 如果是跨页全选，传递筛选条件
      if (selectAllPages) {
        requestData.selectAllPages = true;
        requestData.filters = {
          name: searchText || null,
          weworkUserid: selectedEmployeeUserid || null,
          tagIds: selectedTags.length > 0 ? selectedTags.join(',') : null,
          startDate: dateRange.length === 2 ? dateRange[0].format('YYYY-MM-DD') : null,
          endDate: dateRange.length === 2 ? dateRange[1].format('YYYY-MM-DD') : null,
          onlyNotDeletedByCustomer: customerFilter === 'not_deleted_by_customer' ? true : null
        };
      } else {
        // 否则传递选中的客户ID列表
        requestData.customerFollowIds = selectedRowKeys;
      }

      // 调用批量标签操作API
      const response = await api.post('/customer-follows/batch-tag-operation', requestData);
      
      message.success(`批量${batchOperationType === 'add' ? '添加' : '删除'}标签任务已提交，正在后台处理...`);
      
      closeBatchTagModal();
      
      // 清空选择
      setSelectedRowKeys([]);
      setSelectedRows([]);
      setSelectAllPages(false);
      
      // 刷新列表
      setTimeout(() => {
        fetchCustomers(pagination.current, pagination.pageSize, searchText, customerFilter, selectedEmployeeUserid, selectedTags, dateRange);
      }, 1000);
      
    } catch (error) {
      console.error('批量标签操作失败:', error);
      message.error(error.response?.data?.message || '批量标签操作失败');
    } finally {
      setBatchOperationLoading(false);
    }
  };

  // 获取推送列表
  const fetchSopTemplates = async () => {
    try {
      setTriggerLoading(true);
      const templates = await customerService.getSopTemplates();
      
      // 根据返回数据结构提取模板列表
      let templateList = [];
      if (Array.isArray(templates)) {
        templateList = templates;
      } else if (templates && Array.isArray(templates.content)) {
        templateList = templates.content;
      } else if (templates && typeof templates === 'object') {
        templateList = [templates]; // 单个对象转为数组
      }
      
      // 过滤掉已删除或非活跃的模板，同时确保只有CUSTOMER类型的模板
      const filteredTemplates = templateList.filter(template => 
        !template.deleted && 
        (template.status !== 'inactive' && template.status !== false) && 
        template.targetType === 'CUSTOMER'
      );
      
      console.log('获取到客户类型推送:', filteredTemplates);
      setSopTemplates(filteredTemplates);
    } catch (error) {
      console.error('获取推送列表失败:', error);
      message.error('获取推送列表失败: ' + (error.response?.data?.message || error.message || '未知错误'));
      setSopTemplates([]);
    } finally {
      setTriggerLoading(false);
    }
  };

  // 显示触发SOP的模态框
  const showTriggerSopModal = async (customerId, e) => {
    // 阻止事件冒泡，避免触发行点击事件打开详情抽屉
    if (e) {
      e.stopPropagation();
    }
    setSelectedCustomerId(customerId);
    setSopModalVisible(true);
    fetchSopTemplates();
  };

  // 关闭SOP模态框
  const handleSopModalClose = () => {
    setSopModalVisible(false);
    setSelectedTemplateId(null);
  };

  // 触发SOP
  const handleTriggerSop = async () => {
    if (!selectedTemplateId) {
      message.warning('请选择一个推送');
      return;
    }

    try {
      setTriggerLoading(true);
      
      // 获取选中的跟进关系记录
      const selectedCustomer = customers.find(c => c.id === selectedCustomerId);
      if (!selectedCustomer) {
        throw new Error('未找到对应的客户跟进关系记录');
      }
      
      // 使用系统客户ID作为targetId
      const actualCustomerId = selectedCustomer.customerId;
      // 使用跟进人的企微userid作为operatorId
      const operatorId = selectedCustomer.weworkUserid;
      
      if (!operatorId) {
        throw new Error('跟进人信息不完整，无法触发推送');
      }
      
      console.log('触发SOP参数:', {
        templateId: selectedTemplateId,
        targetType: 'CUSTOMER',
        targetId: actualCustomerId,
        operatorId: operatorId,
        customerName: selectedCustomer.name,
        employeeName: selectedCustomer.employeeName
      });
      
      const response = await customerService.triggerSop(
        selectedTemplateId,
        'CUSTOMER',
        actualCustomerId,  // 使用实际的系统客户ID
        operatorId         // 传递跟进人的企微userid
      );
      
      message.success(`推送触发成功！跟进人：${selectedCustomer.employeeName || operatorId}`);
      setSopModalVisible(false);
      setSelectedTemplateId(null);
    } catch (error) {
      console.error('触发SOP失败:', error);
      message.error('触发SOP失败: ' + (error.response?.data?.message || error.message || '未知错误'));
    } finally {
      setTriggerLoading(false);
    }
  };

  // 新增函数：显示客户SOP执行记录
  const showSopExecutionRecords = (followId, e) => {
    if (e) {
      e.stopPropagation(); // 阻止事件冒泡
    }
    
    // 需要使用系统客户ID而不是跟进关系ID
    const selectedCustomer = customers.find(c => c.id === followId);
    const actualCustomerId = selectedCustomer ? selectedCustomer.customerId : followId;
    
    setSopRecordCustomerId(actualCustomerId);
    setSopRecordDrawerVisible(true);
  };

  // 新增函数：关闭SOP执行记录抽屉
  const handleSopRecordDrawerClose = () => {
    setSopRecordDrawerVisible(false);
  };

  // 表格列定义
  const columns = [
    {
      title: '姓名',
      dataIndex: 'name',
      key: 'name',
      width: 150,
      render: (text, record) => (
        <a onClick={() => showCustomerDetail(record.id)}>
          <Space>
            <Avatar 
              icon={<UserOutlined />} 
              src={record.avatar} 
              size="small"
            />
            {text}
          </Space>
        </a>
      )
    },
    {
      title: '性别',
      dataIndex: 'gender',
      key: 'gender',
      width: 80,
      render: (gender) => GENDER_MAP[gender] || '未知'
    },
    {
      title: '手机号',
      dataIndex: 'phone',
      key: 'phone',
      width: 150,
      render: (text, record) => {
        // 优先显示备注手机号（如果有）
        if (record.remarkMobiles && record.remarkMobiles.length > 0) {
          return (
            <Space>
              <PhoneOutlined />
              <Tooltip title="备注手机号">
                {record.remarkMobiles[0]}
              </Tooltip>
            </Space>
          );
        }
        // 否则显示常规手机号
        return text ? (
          <Space>
            <PhoneOutlined />
            {text}
          </Space>
        ) : '-';
      }
    },
    {
      title: '公司',
      dataIndex: 'company',
      key: 'company',
      ellipsis: true,
    },
    {
      title: '跟进成员',
      dataIndex: 'employeeName',
      key: 'employeeName',
      width: 120,
      render: (text, record) => (
        <Space>
          {text || record.weworkUserid}
        </Space>
      )
    },
    {
      title: '标签',
      dataIndex: 'tags',
      width: 200,
      key: 'tags',
      ellipsis: true,
      render: (tags) => (
        <div style={{ maxWidth: '180px', overflow: 'hidden' }}>
          <Space size={[4, 4]} wrap style={{ display: 'flex', flexWrap: 'wrap' }}>
            {tags && tags.length > 0 ? (
              tags.slice(0, 3).map(tag => (
                <Tag 
                  color={tag.color} 
                  key={tag.id}
                  style={{ 
                    maxWidth: '160px', 
                    overflow: 'hidden', 
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    margin: '2px'
                  }}
                  title={tag.name}
                >
                  <TagOutlined style={{ fontSize: '10px' }} /> {tag.name}
                </Tag>
              ))
            ) : (
              <Text type="secondary" style={{ fontSize: '12px' }}>无标签</Text>
            )}
            {tags && tags.length > 3 && (
              <Tooltip title={tags.slice(3).map(tag => tag.name).join(', ')}>
                <Tag style={{ margin: '2px', fontSize: '11px' }}>+{tags.length - 3}</Tag>
              </Tooltip>
            )}
          </Space>
        </div>
      ),
    },
    {
      title: '添加方式',
      dataIndex: 'addWay',
      key: 'addWay',
      width: 140,
      ellipsis: true,
      // 只有当addWay存在时才显示此列
      render: (addWay) => addWay !== undefined ? 
        <Tooltip title={ADD_WAY_MAP[addWay] || '未知'}>{ADD_WAY_MAP[addWay] || '未知'}</Tooltip> : 
        <Text type="secondary">--</Text>
    },
    {
      title: '添加时间',
      key: 'timeInfo',
      width: 180,
      // 修复时间显示：直接格式化ISO时间字符串，不乘以1000
      render: (_, record) => {
        if (record.createTime) {
          return formatDateTime(record.createTime);
        } else if (record.createdAt) {
          return formatDateTime(record.createdAt);
        }
        return '-';
      }
    },
    {
      title: '操作',
      key: 'action',
      width: 150,
      render: (_, record) => (
        <Space>
          <Button
            type="link"
            onClick={(e) => showTriggerSopModal(record.id, e)}
          >
            触发
          </Button>
          <Button
            type="link"
            onClick={(e) => showSopExecutionRecords(record.id, e)}
          >
            执行记录
          </Button>
        </Space>
      ),
    }
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5}>企微客户管理</Title>
        </Col>
      </Row>

      <Card>
        <div style={{ marginBottom: 16 }}>
          {/* 第一行：客户类型和添加时间 */}
          <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
            <Col flex="auto" style={{ maxWidth: '100px' }}>
              <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>客户类型:</span>
            </Col>
            <Col flex="1" style={{ maxWidth: '200px' }}>
              <Select
                value={customerFilter}
                style={{ width: '100%' }}
                onChange={handleCustomerFilterChange}
                suffixIcon={<FilterOutlined />}
              >
                {CUSTOMER_FILTER_OPTIONS.map(option => (
                  <Option key={option.value} value={option.value}>
                    {option.label}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col flex="auto" style={{ maxWidth: '100px', marginLeft: 24 }}>
              <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>添加时间:</span>
            </Col>
            <Col flex="1" style={{ maxWidth: '300px' }}>
              <RangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
                placeholder={['开始日期', '结束日期']}
                style={{ width: '100%' }}
              />
            </Col>
            <Col flex="1" />
          </Row>
          
          {/* 第二行：跟进成员、标签、搜索客户 */}
          <Row gutter={16} style={{ marginBottom: 16 }} align="middle">
            <Col flex="auto" style={{ maxWidth: '100px' }}>
              <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>跟进成员:</span>
            </Col>
            <Col flex="1" style={{ maxWidth: '200px' }}>
              <EmployeeSelect
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                placeholder="请选择跟进成员"
                allowClear
                style={{ width: '100%' }}
                showDisplayModeSwitch={false}
                defaultDisplayMode="department"
              />
            </Col>
            <Col flex="auto" style={{ maxWidth: '60px', marginLeft: 24 }}>
              <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>标签:</span>
            </Col>
            <Col flex="1" style={{ maxWidth: '250px' }}>
              <Select
                mode="multiple"
                value={selectedTags}
                onChange={handleTagsChange}
                placeholder="请选择标签"
                allowClear
                style={{ width: '100%' }}
                showSearch
                optionFilterProp="children"
                filterOption={(input, option) => {
                  // 处理OptGroup标签
                  if (option.type === 'group') {
                    return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
                  }
                  // 处理Option标签
                  return option.children ? 
                    option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 : 
                    false;
                }}
                dropdownMatchSelectWidth={false}
                dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
                notFoundContent={availableTags.length === 0 ? "加载中..." : "未找到匹配标签"}
              >
                {groupTagsByGroup(availableTags).map(group => (
                  <Select.OptGroup key={group.groupId} label={group.groupName}>
                    {group.tags.map(tag => (
                      <Option key={tag.id} value={tag.id}>
                        {tag.tagName || tag.name}
                        {tag.source === 'WEWORK' && <Tag color="blue" style={{ marginLeft: 8, fontSize: '10px' }}>企微</Tag>}
                      </Option>
                    ))}
                  </Select.OptGroup>
                ))}
              </Select>
            </Col>
            <Col flex="auto" style={{ maxWidth: '100px', marginLeft: 24 }}>
              <span style={{ fontSize: '14px', color: '#666', whiteSpace: 'nowrap' }}>搜索客户:</span>
            </Col>
            <Col flex="1" style={{ maxWidth: '250px' }}>
              <Input
                placeholder="搜索客户"
                allowClear
                style={{ width: '100%' }}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </Col>
            <Col flex="1" />
          </Row>

          {/* 第三行：操作按钮 */}
          <Row gutter={16} style={{ marginBottom: 16 }}>
            <Col>
              <Space>
                <Button 
                  type="primary" 
                  onClick={handleApplyFilters}
                >
                  筛选
                </Button>
                <Button onClick={handleResetFilters}>
                  重置筛选
                </Button>
                <Button 
                  type="primary" 
                  icon={<SyncOutlined />} 
                  loading={syncLoading}
                  onClick={handleSync}
                >
                  同步企业微信客户
                </Button>
              </Space>
            </Col>
          </Row>

          {/* 批量操作区域 */}
          {(selectedRowKeys.length > 0 || selectAllPages) && (
            <Row style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#f0f5ff', borderRadius: '6px' }}>
              <Col span={24}>
                <Space>
                  <span style={{ color: '#1890ff' }}>
                    {selectAllPages ? 
                      `已选择所有符合条件的客户 (当前页显示${customers.length}条)` : 
                      `已选择 ${selectedRowKeys.length} 个客户`
                    }
                  </span>
                  <Button 
                    type="primary" 
                    size="small" 
                    icon={<PlusOutlined />}
                    onClick={() => showBatchTagModal('add')}
                  >
                    批量添加标签
                  </Button>
                  <Button 
                    size="small" 
                    icon={<DeleteOutlined />}
                    onClick={() => showBatchTagModal('remove')}
                  >
                    批量删除标签
                  </Button>
                  <Button 
                    size="small" 
                    type="text"
                    onClick={() => {
                      setSelectedRowKeys([]);
                      setSelectedRows([]);
                      setSelectAllPages(false);
                    }}
                  >
                    取消选择
                  </Button>
                </Space>
              </Col>
            </Row>
          )}
        </div>
        <Table
          columns={columns}
          dataSource={customers}
          rowKey="id"
          loading={loading}
          scroll={{ x: 'max-content' }}
          rowSelection={{
            selectedRowKeys,
            onChange: handleSelectChange,
            onSelectAll: (selected, selectedRows, changeRows) => {
              // 处理全选按钮
              if (selected && selectedRowKeys.length === customers.length) {
                // 如果当前页已经全选，询问是否跨页全选
                handleSelectAllPages(true);
              }
            },
            columnTitle: (
              <div>
                <Checkbox
                  indeterminate={selectedRowKeys.length > 0 && selectedRowKeys.length < customers.length && !selectAllPages}
                  checked={selectAllPages || selectedRowKeys.length === customers.length}
                  onChange={(e) => {
                    if (e.target.checked) {
                      if (selectedRowKeys.length === customers.length) {
                        // 当前页已全选，切换到跨页全选
                        handleSelectAllPages(true);
                      } else {
                        // 选中当前页所有数据
                        const allKeys = customers.map(item => item.id);
                        setSelectedRowKeys(allKeys);
                        setSelectedRows(customers.slice());
                      }
                    } else {
                      // 取消所有选择
                      handleSelectAllPages(false);
                    }
                  }}
                />
                {selectedRowKeys.length === customers.length && !selectAllPages && (
                  <Tooltip title="点击选择所有页面的客户">
                    <Button 
                      type="link" 
                      size="small" 
                      style={{ fontSize: '12px', padding: '0 4px' }}
                      onClick={() => handleSelectAllPages(true)}
                    >
                      全选所有页
                    </Button>
                  </Tooltip>
                )}
              </div>
            ),
            renderCell: (checked, record, index, originNode) => {
              return (
                <div onClick={(e) => e.stopPropagation()}>
                  {originNode}
                </div>
              );
            }
          }}
          pagination={{
            ...pagination,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total) => `共 ${total} 条记录`,
          }}
          onChange={handleTableChange}
          onRow={(record) => ({
            onClick: (e) => {
              // 如果点击的不是checkbox区域，则显示详情
              if (!e.target.closest('.ant-checkbox-wrapper') && !e.target.closest('.ant-checkbox')) {
                showCustomerDetail(record.id);
              }
            }
          })}
        />
      </Card>

      {/* 客户详情抽屉 */}
      <Drawer
        title={drawerCustomer ? `${drawerCustomer.name} - 跟进关系详情` : '客户跟进关系详情'}
        width={700}
        open={drawerVisible}
        onClose={handleDrawerClose}
        destroyOnClose
        extra={
          drawerCustomer && (
            <Space>
              <Button
                type="link"
                icon={<PlayCircleOutlined />}
                onClick={() => showTriggerSopModal(drawerCustomer.id)}
              >
                触发
              </Button>
              <Button
                type="link"
                icon={<HistoryOutlined />}
                onClick={() => showSopExecutionRecords(drawerCustomer.id)}
              >
                执行记录
              </Button>
            </Space>
          )
        }
      >
        {drawerLoading ? (
          <Card loading={true} />
        ) : drawerCustomer ? (
          <Tabs defaultActiveKey="1">
            <TabPane tab="基本信息" key="1">
              <Card bordered={false}>
                <Row gutter={16}>
                  <Col span={6}>
                    <Avatar 
                      size={80} 
                      icon={<UserOutlined />}
                      src={drawerCustomer.avatar}
                    />
                  </Col>
                  <Col span={18}>
                    <Title level={4}>{drawerCustomer.name}</Title>
                    {drawerCustomer.company && (
                      <Paragraph>
                        {drawerCustomer.company}
                      </Paragraph>
                    )}
                    {drawerCustomer.employeeName && (
                      <Paragraph>
                        <Space>
                          <span>跟进成员：{drawerCustomer.employeeName}</span>
                        </Space>
                      </Paragraph>
                    )}
                    <Space size={[0, 4]} wrap>
                      {drawerCustomer.tags && drawerCustomer.tags.map(tag => (
                        <Tag color={tag.color} key={tag.id}>
                          <TagOutlined /> {tag.name}
                        </Tag>
                      ))}
                    </Space>
                  </Col>
                </Row>
                
                <Divider />
                
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Space>
                      <Text type="secondary">状态:</Text>
                      <Tag color={
                        drawerCustomer.status === 'active' ? 'success' :
                        drawerCustomer.status === 'lead' ? 'processing' :
                        drawerCustomer.status === 'customer' ? 'success' : 'default'
                      }>
                        {
                          drawerCustomer.status === 'active' ? '活跃' :
                          drawerCustomer.status === 'inactive' ? '不活跃' :
                          drawerCustomer.status === 'lead' ? '潜在' :
                          drawerCustomer.status === 'customer' ? '客户' : '未知'
                        }
                      </Tag>
                    </Space>
                  </Col>
                  <Col span={12}>
                    <Space>
                      <Text type="secondary">性别:</Text>
                      <Text>{GENDER_MAP[drawerCustomer.gender] || '未知'}</Text>
                    </Space>
                  </Col>
                  
                  {drawerCustomer.phone && (
                    <Col span={12}>
                      <Space>
                        <PhoneOutlined />
                        <Text>{drawerCustomer.phone}</Text>
                      </Space>
                    </Col>
                  )}
                  
                  {/* 备注手机号显示 */}
                  {drawerCustomer.remarkMobiles && drawerCustomer.remarkMobiles.length > 0 && (
                    <Col span={12}>
                      <Space>
                        <PhoneOutlined />
                        <Text type="secondary">备注手机号:</Text>
                        <Text>{drawerCustomer.remarkMobiles.join(', ')}</Text>
                      </Space>
                    </Col>
                  )}
                  
                  {/* 添加方式显示 */}
                  {drawerCustomer.addWay !== undefined && (
                    <Col span={24}>
                      <Space>
                        <Text type="secondary">添加方式:</Text>
                        <Text>{ADD_WAY_MAP[drawerCustomer.addWay] || '未知'}</Text>
                      </Space>
                    </Col>
                  )}
                  
                  {/* 新增备注字段展示 */}
                  {drawerCustomer.remark && (
                    <Col span={24}>
                      <Paragraph>
                        <Text type="secondary">备注:</Text>
                        <br />
                        {drawerCustomer.remark}
                      </Paragraph>
                    </Col>
                  )}
                  
                  {/* 新增描述字段展示 */}
                  {drawerCustomer.description && (
                    <Col span={24}>
                      <Paragraph>
                        <Text type="secondary">描述:</Text>
                        <br />
                        {drawerCustomer.description}
                      </Paragraph>
                    </Col>
                  )}
                </Row>
                
                <Divider />
                
                <Row gutter={16}>
                  {drawerCustomer.createTime && (
                    <Col span={12}>
                      <Text type="secondary">添加时间:</Text>
                      <Paragraph>
                        {formatDateTime(new Date(drawerCustomer.createTime * 1000))}
                      </Paragraph>
                    </Col>
                  )}
                  <Col span={12}>
                    <Text type="secondary">最后更新:</Text>
                    <Paragraph>
                      {formatDateTime(drawerCustomer.updatedAt)}
                    </Paragraph>
                  </Col>
                </Row>
                
                {(drawerCustomer.source || '').toUpperCase() === 'WEWORK' && (
                  <>
                    <Divider>企业微信信息</Divider>
                    <Descriptions bordered size="small" column={1}>
                      <Descriptions.Item label="客户类型">{CUSTOMER_TYPE_MAP[drawerCustomer.type] || '-'}</Descriptions.Item>
                      <Descriptions.Item label="添加方式">{ADD_WAY_MAP[drawerCustomer.addWay] || '-'}</Descriptions.Item>
                      {drawerCustomer.createTime && (
                        <Descriptions.Item label="添加时间">
                          {formatDateTime(new Date(drawerCustomer.createTime * 1000))}
                        </Descriptions.Item>
                      )}
                      {drawerCustomer.remarkMobiles && drawerCustomer.remarkMobiles.length > 0 && (
                        <Descriptions.Item label="备注手机号">
                          {drawerCustomer.remarkMobiles.join(', ')}
                        </Descriptions.Item>
                      )}
                      <Descriptions.Item label="同步时间">
                        {formatDateTime(drawerCustomer.syncedAt)}
                      </Descriptions.Item>
                    </Descriptions>
                  </>
                )}
              </Card>
            </TabPane>

            <TabPane tab="推送执行记录" key="2">
              {drawerCustomer && (
                <SopExecutionRecords 
                  targetId={drawerCustomer.customerId} 
                  targetType="CUSTOMER"
                  title="当前客户的推送执行记录"
                />
              )}
            </TabPane>
          </Tabs>
        ) : (
          <Empty description="未找到客户信息" />
        )}
      </Drawer>

      {/* 添加推送选择模态框 */}
      <Modal
        title="触发推送"
        open={sopModalVisible}
        onCancel={handleSopModalClose}
        footer={[
          <Button key="cancel" onClick={handleSopModalClose}>
            取消
          </Button>,
          <Button 
            key="trigger" 
            type="primary" 
            loading={triggerLoading}
            onClick={handleTriggerSop}
          >
            确认触发
          </Button>
        ]}
        width={600}
      >
        <Spin spinning={triggerLoading}>
          {/* 操作提示信息 */}
          <div style={{ 
            marginBottom: 20, 
            padding: '12px 16px', 
            backgroundColor: '#e6f7ff', 
            border: '1px solid #91d5ff',
            borderRadius: '6px'
          }}>
            <div style={{ fontSize: '14px', fontWeight: 500, color: '#0050b3', marginBottom: 8 }}>
              📋 操作说明
            </div>
            <div style={{ fontSize: '13px', color: '#096dd9', lineHeight: '1.6' }}>
              触发推送时，系统将：<br/>
              1. 根据推送模板要求，自动为客户打上相应的标签<br/>
              2. 基于当前跟进人信息启动推送流程（有一定延迟）<br/>
              3. 后续推送动作将由当前跟进人执行
            </div>
          </div>
          
          <div style={{ marginBottom: 16 }}>
            <Text strong>请选择要触发的推送：</Text>
          </div>
          
          {sopTemplates.length > 0 ? (
            <List
              bordered
              dataSource={sopTemplates}
              renderItem={template => (
                <List.Item
                  onClick={() => setSelectedTemplateId(template.id)}
                  style={{ 
                    cursor: 'pointer',
                    backgroundColor: selectedTemplateId === template.id ? '#f0f5ff' : 'transparent'
                  }}
                >
                  <Space>
                    <Radio checked={selectedTemplateId === template.id} />
                    <div>
                      <Text strong>{template.name}</Text>
                      {template.description && (
                        <div>
                          <Text type="secondary">{template.description}</Text>
                        </div>
                      )}
                    </div>
                  </Space>
                </List.Item>
              )}
            />
          ) : (
            <Empty description="暂无可用的推送" />
          )}
        </Spin>
      </Modal>

      {/* 新增：SOP执行记录抽屉 */}
      <Drawer
        title="推送执行记录"
        width={700}
        open={sopRecordDrawerVisible}
        onClose={handleSopRecordDrawerClose}
        destroyOnClose
      >
        {sopRecordCustomerId && (
          <SopExecutionRecords 
            targetId={sopRecordCustomerId} 
            targetType="CUSTOMER"
            title="客户推送执行记录"
          />
        )}
      </Drawer>

      {/* 批量标签操作弹窗 */}
      <Modal
        title={`批量${batchOperationType === 'add' ? '添加' : '删除'}标签`}
        open={batchTagModalVisible}
        onCancel={closeBatchTagModal}
        footer={[
          <Button key="cancel" onClick={closeBatchTagModal}>
            取消
          </Button>,
          <Button 
            key="execute" 
            type="primary" 
            loading={batchOperationLoading}
            onClick={executeBatchTagOperation}
          >
            确认{batchOperationType === 'add' ? '添加' : '删除'}
          </Button>
        ]}
        width={600}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ marginBottom: 8, color: '#666' }}>
            操作范围: {selectAllPages ? '所有符合筛选条件的客户' : `${selectedRowKeys.length} 个选中的客户`}
          </div>
          <div style={{ marginBottom: 16, padding: '8px 12px', backgroundColor: '#f6f8fa', borderRadius: '4px', fontSize: '12px', color: '#666' }}>
            {batchOperationType === 'add' ? 
              '提示: 系统会自动跳过已经拥有选中标签的客户，避免重复操作' :
              '提示: 系统会自动跳过没有选中标签的客户，避免无效操作'
            }
          </div>
        </div>
        
        <div>
          <div style={{ marginBottom: 8, fontWeight: 500 }}>
            选择要{batchOperationType === 'add' ? '添加' : '删除'}的标签:
          </div>
          <Select
            mode="multiple"
            value={batchSelectedTags}
            onChange={setBatchSelectedTags}
            placeholder={`请选择要${batchOperationType === 'add' ? '添加' : '删除'}的标签`}
            style={{ width: '100%' }}
            showSearch
            filterOption={(input, option) => {
              // 处理OptGroup标签
              if (option.type === 'group') {
                return option.label.toLowerCase().indexOf(input.toLowerCase()) >= 0;
              }
              // 处理Option标签
              return option.children ? 
                option.children.toString().toLowerCase().indexOf(input.toLowerCase()) >= 0 : 
                false;
            }}
            maxTagCount={5}
            dropdownMatchSelectWidth={false}
            dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
            notFoundContent={availableTags.length === 0 ? "加载中..." : "未找到匹配标签"}
          >
            {groupTagsByGroup(availableTags).map(group => (
              <Select.OptGroup key={group.groupId} label={group.groupName}>
                {group.tags.map(tag => (
                  <Option key={tag.id} value={tag.id}>
                    {tag.tagName || tag.name}
                    {tag.source === 'WEWORK' && <Tag color="blue" style={{ marginLeft: 8, fontSize: '10px' }}>企微</Tag>}
                  </Option>
                ))}
              </Select.OptGroup>
            ))}
          </Select>
        </div>
        
        <div style={{ marginTop: 16, padding: '8px 12px', backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: '4px' }}>
          <div style={{ fontWeight: 500, color: '#fa8c16', marginBottom: 4 }}>
            ⚠️ 重要说明
          </div>
          <div style={{ fontSize: '12px', color: '#666', lineHeight: '1.5' }}>
            • 批量操作将异步执行，可能需要一些时间完成<br/>
            • 操作完成后会自动触发相关的标签变更事件
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Customers;